import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import CascadingDropdowns from './CascadingDropdowns';
import GenericFileUpload from '../../../../../components/common/GenericFileUpload';
import useFileOperations from '../hooks/useFileOperations';
import ChassisDropdown from '../../../../../components/common/ChassisDropdown';
import ItemCodeDropdown from '../../../../../components/common/ItemCodeDropdown';
import FaultModesDropdown from '../../../../../components/common/FaultModeDropdown';
import ReasonDialog from './ReasonDialog';
import { formatDateOnly } from '../../../../../utils/dateUtils';
import { selectUserRoles } from '../../../../../features/auth/authSlice';
import { Roles } from '../../../../../utils/roleEnums';
import { useGetDealerPaymentCurrencyQuery } from '../../../../../api/endpoints/dealerAddressBrandPayment';
import { InputTextarea } from 'primereact/inputtextarea';
import PreApprovalCommentsTable from '../components/ApprovalComponentsTable';
import { useLazyGetChassisDetailQuery } from '../../../../../api/customerDeclarationApi';
import {DealerDocumentsSection , WarrantyDocumentsSection} from './DocumentSections';
import { MODULE_CODES } from '../hooks/useFileOperations';


// Status enum
const STATUS = {
    DRAFT: 10,
    WAITING: 20,
    RETURNED: 15,
    ACCEPTED: 30,
    REJECTED: 40
};

const textareaStyle = {
    resize: 'vertical',
    overflow: 'auto',
    minHeight: '80px',
    maxHeight: '300px'
};

const readOnlyTextStyle = {
    overflow: 'auto',
    textOverflow: 'unset',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    minHeight: '38px',
    maxHeight: '150px',
    background: '#f4f4f4',      
    color: '#6c757d',           
    cursor: 'not-allowed',      
    resize: 'none',
    opacity: 0.7,              
};

const PreApprovalForm = ({ dialogVisible, editMode, submitted, hideDialog, formData, handleChange, handleSubmit, handleSendForApproval, handleApprove, handleReject, handleReturn, currentPreApproval , isStatusChanging }) => {
    const { th } = useLanguage();
    const userRoles = useSelector(selectUserRoles);
   const isWarrantyTeamOnly =
    Array.isArray(userRoles) &&
    (userRoles.includes(Roles.WARRANTY_TEAM) ||
        userRoles.includes(Roles.WARRANTY_MANAGER) ||
        userRoles.includes(Roles.SSH_MANAGER) ||
        userRoles.includes(Roles.SSH_DIRECTOR) ||
        userRoles.includes(Roles.SSH_GENERAL_MANAGER) ||
        userRoles.includes(Roles.CEO));

    const isAdmin = Array.isArray(userRoles) && userRoles.includes(Roles.ADMIN);

    const authorizationCheckforPreStock = isWarrantyTeamOnly || isAdmin;

    const STATUS_CONFIG = {
        [STATUS.DRAFT]: { label: th('preApproval.status.draft'), severity: 'info', icon: 'pi-file-edit' },
        [STATUS.WAITING]: { label: th('preApproval.status.waitingApproval'), severity: 'warning', icon: 'pi-clock' },
        [STATUS.RETURNED]: { label: th('preApproval.status.returned'), severity: 'danger', icon: 'pi-replay' },
        [STATUS.ACCEPTED]: { label: th('preApproval.status.accepted'), severity: 'success', icon: 'pi-check-circle' },
        [STATUS.REJECTED]: { label: th('preApproval.status.rejected'), severity: 'danger', icon: 'pi-times-circle' }
    };

    const currentStatus = editMode ? formData?.status || STATUS.DRAFT : STATUS.DRAFT;
    const currentStatusConfig = STATUS_CONFIG[currentStatus];
    const isCompletelyReadOnly = editMode && (currentStatus === STATUS.ACCEPTED || currentStatus === STATUS.REJECTED);

    const isAdminActingAsWarranty =
    isAdmin && currentStatus === STATUS.WAITING;

    const isWarrantyTeam = isWarrantyTeamOnly || isAdminActingAsWarranty;

    const { data: currencyData } = useGetDealerPaymentCurrencyQuery();
    const { canCreateCustomerDeclaration } = usePermissions();
    const currency = currencyData?.currency || 'TRY';

    

    const [resetDropdowns, setResetDropdowns] = useState(false);
    const isFirstRender = useRef(true);
    const toast = useRef(null);
    const [rejectReason, setRejectReason] = useState('');
    const [returnReason, setReturnReason] = useState('');
    const [reasonDialogVisible, setReasonDialogVisible] = useState(false);
    const [reasonDialogType, setReasonDialogType] = useState('reject');
    const isFieldEdit = !editMode || formData?.status === STATUS.DRAFT;

    const [chassisDetailData, setChassisDetailData] = useState(null);
    const [triggerGetChassisDetail] = useLazyGetChassisDetailQuery();
    

    const {
    newFiles,
    setNewFiles,
    dealerDocuments,       
    warrantyDocuments,     
    isProcessingFiles,
    isLoadingDocuments,
    canUpload,     
    handleFileUpload,        
    createEnhancedHandler,
    handleDownloadDocument,
    handleDownloadAllAsZip,
    handleDeleteExistingDocument
} = useFileOperations(currentPreApproval, editMode, dialogVisible, toast, isWarrantyTeam, isAdmin);

    const InfoCardItem = ({ icon, label, value }) => (
        <div className="col-12 md:col-3">
            <div className="mb-2 md:mb-0">
                <span className="font-semibold text-600 block text-sm mb-1">
                    <i className={`${icon} mr-1`} /> {label}
                </span>
                <span className="text-900 font-medium">{value}</span>
            </div>
        </div>
    );

    const fetchChassisDetails = useCallback(async () => {
        if (!formData.chassisNo || !dialogVisible) {
            setChassisDetailData(null);
            return;
        }

        try {
            const result = await triggerGetChassisDetail({ chassisNumber: formData.chassisNo }).unwrap();
            setChassisDetailData(result);
        } catch (error) {
            console.error('Şasi detay çekme hatası:', error);
            setChassisDetailData(null);
        }
    }, [formData.chassisNo, dialogVisible, triggerGetChassisDetail]);

    useEffect(() => {
        fetchChassisDetails();
    }, [fetchChassisDetails]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('tr-TR');
    };

    useEffect(() => {
        if (dialogVisible) {
            if (!editMode) {
                setResetDropdowns(true);
                const timer = setTimeout(() => setResetDropdowns(false), 500);
                return () => clearTimeout(timer);
            } else {
                setResetDropdowns(false);
            }
        } else {
            setResetDropdowns(false);
        }
        isFirstRender.current = false;
    }, [dialogVisible, editMode]);

    const handleShowRejectDialog = () => {
        setReasonDialogType('reject');
        setReasonDialogVisible(true);
    };
    const handleShowReturnDialog = () => {
        setReasonDialogType('return');
        setReasonDialogVisible(true);
    };

    const handleReasonConfirm = async (reason) => {
    // Dosya varsa önce yükle (sadece garanti ekibi için geçerli)
    if (newFiles.length > 0) {
        await handleFileUpload();
    }

    if (reasonDialogType === 'reject') {
        handleReject(reason);
        setRejectReason('');
    } else {
        handleReturn(reason);
        setReturnReason('');
    }
    setReasonDialogVisible(false);
};

    const enhancedHandleSubmit = editMode ? createEnhancedHandler(handleSubmit) : handleSubmit;
    const enhancedHandleSendForApproval = editMode ? createEnhancedHandler(handleSendForApproval) : handleSendForApproval;
    const enhancedHandleApprove = editMode ? createEnhancedHandler(handleApprove) : handleApprove;

    const hasAnyFile = () => {
        if (!editMode) return true;
        return newFiles.length > 0 || dealerDocuments.length > 0 || warrantyDocuments.length > 0;
    };
    const guardedHandleSubmit = async () => {
        if (editMode && !hasAnyFile()) {
            toast.current?.show({ severity: 'warn', summary: th('preApproval.messages.missingInfoSummary'), detail: th('preApproval.messages.missingFile'), life: 3000 });
            return;
        }
        await enhancedHandleSubmit();
    };

    const guardedHandleSendForApproval = async () => {
        if (editMode && !hasAnyFile()) {
            toast.current?.show({ severity: 'warn', summary: th('preApproval.messages.missingInfoSummary'), detail: th('preApproval.messages.missingFileApproval'), life: 3000 });
            return;
        }
        await enhancedHandleSendForApproval();
    };

  

    const getFieldDisabledState = useCallback(
        (fieldName) => {
            if (!editMode) return false;
            switch (currentStatus) {
                case STATUS.WAITING:
                case STATUS.ACCEPTED:
                case STATUS.REJECTED:
                    return true;
                case STATUS.RETURNED:
                    if (fieldName === 'chassisNo' || fieldName === 'preAppNo') return true;
                    return editMode ? isProcessingFiles : false;
                case STATUS.DRAFT:
                default:
                    return editMode ? isProcessingFiles : false;
            }
        },
        [editMode, currentStatus, isProcessingFiles]
    );

    const handleTextareaDoubleClick = (e) => e.target.select();

    const warrantyStatus = React.useMemo(() => {
        if (!formData.warrantyEndDate) return null;
        return new Date(formData.warrantyEndDate) >= new Date() ? 'active' : 'expired';
    }, [formData.warrantyEndDate]);

    const renderDialogFooter = () => {
        const s = { textShadow: '0px 1px 2px rgba(0, 0, 0, 0.5)' };
        if (!canCreateCustomerDeclaration()) return;
        if (!editMode) {
            return (
                <div className="flex justify-content-end">
                    <Button label={th('preApproval.buttons.saveAndClose')} icon="pi pi-check" onClick={() => handleSubmit(newFiles)} style={s} />
                    <Button label={th('fullServiceInvoiceKm.buttons.saveAndSend')} icon="pi pi-check" onClick={() => handleSendForApproval(newFiles)} style={s} />
                </div>
            );
        }
        switch (currentStatus) {
            case STATUS.DRAFT:
                return (
                    <div className="flex justify-content-end">
                        <span className="p-buttonset">
                            <Button label={th('preApproval.buttons.save')} icon="pi pi-save" className="p-button-secondary" onClick={guardedHandleSubmit} style={s} loading={isProcessingFiles} disabled={isProcessingFiles} />
                            <Button label={th('preApproval.buttons.sendForApproval')} icon="pi pi-check" onClick={guardedHandleSendForApproval} style={{ minWidth: '170px', ...s }} loading={isProcessingFiles} disabled={isProcessingFiles} />
                        </span>
                    </div>
                );
            case STATUS.WAITING:
                return (
                    <div className="flex justify-content-end">
                        {authorizationCheckforPreStock && (
                            <span className="p-buttonset">
                                <Button label={th('preApproval.buttons.reject')} icon="pi pi-times" className="p-button-danger" onClick={handleShowRejectDialog} style={s} disabled={isProcessingFiles || isStatusChanging} loading={ isStatusChanging} />
                                <Button label={th('preApproval.buttons.return')} icon="pi pi-reply" className="p-button-warning" onClick={handleShowReturnDialog} style={s} disabled={isProcessingFiles || isStatusChanging}  loading={ isStatusChanging} />
                                <Button label={th('preApproval.buttons.approve')} icon="pi pi-check" className="p-button-success" onClick={enhancedHandleApprove} style={s} loading={isProcessingFiles || isStatusChanging} disabled={isProcessingFiles || isStatusChanging } />
                            </span>
                        )}
                    </div>
                );
            case STATUS.RETURNED:
                return (
                    <div className="flex justify-content-end">
                        <span className="p-buttonset">
                            <Button label={th('preApproval.buttons.save')} icon="pi pi-save" className="p-button-secondary" onClick={guardedHandleSubmit} style={s} loading={isProcessingFiles || isStatusChanging} disabled={isProcessingFiles || isStatusChanging} />
                            <Button label={th('preApproval.buttons.sendForApproval')} icon="pi pi-check" onClick={guardedHandleSendForApproval} style={{ minWidth: '170px', ...s }} loading={isProcessingFiles || isStatusChanging} disabled={isProcessingFiles || isStatusChanging}  />
                        </span>
                    </div>
                );
            case STATUS.ACCEPTED:
            case STATUS.REJECTED:
                return (
                    <div className="flex justify-content-end">
                        <Button label={th('preApproval.buttons.close')} icon="pi pi-times" className="p-button-secondary" onClick={hideDialog} style={s} />
                    </div>
                );
            default:
                return null;
        }
    };

    const dialogHeader = (
        <div className="flex align-items-center justify-content-between">
            <span>{editMode ? th('preApproval.form.title') : th('preApproval.form.newTitle')}</span>
            {editMode && currentStatusConfig && <Tag value={currentStatusConfig.label} severity={currentStatusConfig.severity} icon={`pi ${currentStatusConfig.icon}`} />}
        </div>
    );

    return (
        <>
            <Dialog visible={dialogVisible} style={{ width: '85%', maxWidth: '1200px' }} header={dialogHeader} modal className="p-fluid preapproval-dialog" footer={renderDialogFooter()} onHide={hideDialog} maximizable blockScroll>
                <Toast ref={toast} />

                {editMode && currentStatus === STATUS.RETURNED && <Message severity="info" text={th('preApproval.messages.returnedRecord')} className="w-full mb-4" icon="pi pi-info-circle" />}
                {isCompletelyReadOnly && (
                    <Message severity="warn" text={currentStatus === STATUS.ACCEPTED ? th('preApproval.messages.readOnlyAccepted') : th('preApproval.messages.readOnlyRejected')} className="w-full mb-4" icon="pi pi-exclamation-triangle" />
                )}
                {editMode && isProcessingFiles && <Message severity="info" text={th('preApproval.messages.processingFiles')} className="w-full mb-4" icon="pi pi-spin pi-spinner" />}

                {!isFieldEdit && (
                    <>
                        <h3 className="section-title">
                            <i className="pi pi-info-circle mr-2"></i>
                            {th('preApproval.messages.basicInfoTitle')}
                        </h3>
                        <Divider />
                    </>
                )}

                <div className="grid">
                    {/* Ön Onay No */}
                    {!isFieldEdit && (
                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label className="font-bold block mb-2">
                                    <i className="pi pi-hashtag mr-1"></i>
                                    {th('preApproval.form.preApprovalNoLabel')}
                                </label>
                                <InputText value={formData.preAppNo} disabled className="w-full" title={formData.preAppNo || ''} />
                            </div>
                        </div>
                    )}

                    {/* Şasi No */}
                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="chassisNo" className="font-bold block mb-2">
                                <i className="pi pi-car mr-1"></i>
                                {th('preApproval.form.chassisNoLabel')} *
                            </label>
                            {isFieldEdit ? (
                                <ChassisDropdown
                                    value={formData.chassisNo}
                                    onChange={(v) => handleChange('chassisNo', v)}
                                    className={submitted && !formData.chassisNo ? 'p-invalid' : ''}
                                    disabled={getFieldDisabledState('chassisNo')}
                                    placeholder={th('preApproval.form.placeholders.chassisNo')}
                                />
                            ) : (
                                <InputText value={formData.chassisNo || ''} disabled className="w-full" title={formData.chassisNo || ''} />
                            )}
                            {submitted && !formData.chassisNo && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('preApproval.form.validation.chassisNoRequired')}
                                </small>
                            )}
                        </div>
                    </div>

                    {/* Şasi Bilgileri Display Panel */}
                    {formData.chassisNo && chassisDetailData && (
                        <div className="col-12">
                            <div className="surface-50 p-3 border-round border-1 border-200 mb-2">
                                <div className="grid">
                                    <InfoCardItem icon="pi pi-info-circle" label={th('warranty.customerDeclaration.dialog.fields.chassisDescription')} value={chassisDetailData?.chassisNumber || formData.chassisNumber || '-'} />
                                    <InfoCardItem icon="pi pi-stopwatch" label={th('warranty.customerDeclaration.dialog.fields.lastKmInfo')} value={chassisDetailData?.lastKmInfo?.toLocaleString('tr-TR') || '-'} />
                                    <InfoCardItem icon="pi pi-calendar-plus" label={th('warranty.customerDeclaration.dialog.fields.warrantyBeginDate')} value={formatDate(chassisDetailData?.warrantyBeginDate || formData.warrantyStartDate)} />
                                    <InfoCardItem icon="pi pi-calendar-minus" label={th('warranty.customerDeclaration.dialog.fields.warrantyEndDate')} value={formatDate(chassisDetailData?.warrantyEndDate || formData.warrantyEndDate)} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Plaka No */}
                    {!isFieldEdit && formData.plateNo && (
                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label className="font-bold block mb-2">
                                    <i className="pi pi-id-card mr-1"></i>
                                    {th('preApproval.form.plateNoLabel')}
                                </label>
                                <InputText value={formData.plateNo || '-'} disabled className="w-full" title={formData.plateNo || ''} />
                            </div>
                        </div>
                    )}

                    {!isFieldEdit && (
                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label className="font-bold block mb-2">
                                    <i className="pi pi-car text-primary mr-2"></i>
                                    {th('preApproval.form.currentKmLabel')}
                                </label>
                                <InputText
                                    id="currentKm"
                                    value={formData.currentKm}
                                    onChange={(e) => handleChange('currentKm', e.target.value)}
                                    className={submitted && !formData.currentKm ? 'p-invalid' : ''}
                                    placeholder={th('preApproval.form.currentKmLabel')}
                                    disabled={editMode && currentStatus !== STATUS.DRAFT}
                                />
                            </div>
                        </div>
                    )}

                    {/* Tamir Tarihi */}
                    {!isFieldEdit && (
                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label className="font-bold block mb-2">
                                    <i className="pi pi-wrench mr-1"></i>
                                    {th('preApproval.form.repairDateLabel')}
                                </label>
                                <Calendar value={formData.repairDate ? new Date(formData.repairDate) : null} disabled dateFormat="dd/mm/yy" showIcon className="w-full" />
                            </div>
                        </div>
                    )}

                    {/* Arıza Bilgileri */}
                    {isFieldEdit && (
                        <>
                            <div className="col-12">
                                <h3 className="section-title mt-3">
                                    <i className="pi pi-exclamation-triangle mr-2"></i>
                                    {th('preApproval.form.failureInfoTitle')}
                                </h3>
                                <Divider />
                            </div>
                            <div className="col-12 md:col-6">
                                <div className="field mb-3">
                                    <label htmlFor="failureDate" className="font-bold block mb-2">
                                        <i className="pi pi-calendar mr-1"></i>
                                        {th('preApproval.form.failureDateLabel')} *
                                    </label>
                                    <Calendar
                                        id="failureDate"
                                        value={formData.failureDate}
                                        onChange={(e) => handleChange('failureDate', e.value)}
                                        className={submitted && !formData.failureDate ? 'p-invalid' : ''}
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        placeholder={th('preApproval.form.placeholders.failureDate')}
                                        disabled={getFieldDisabledState('failureDate')}
                                    />
                                    {submitted && !formData.failureDate && (
                                        <small className="p-error">
                                            <i className="pi pi-exclamation-circle mr-1"></i>
                                            {th('preApproval.form.validation.failureDateRequired')}
                                        </small>
                                    )}
                                </div>
                            </div>
                            <div className="col-12 md:col-6">
                                <div className="field mb-3">
                                    <label htmlFor="failureKM" className="font-bold block mb-2">
                                        <i className="pi pi-map-marker mr-1"></i>
                                        {th('preApproval.form.failureKmLabel')} *
                                    </label>
                                    <InputText
                                        id="failureKM"
                                        value={formData.failureKM}
                                        onChange={(e) => handleChange('failureKM', e.target.value)}
                                        className={submitted && !formData.failureKM ? 'p-invalid' : ''}
                                        placeholder={th('preApproval.form.placeholders.failureKm')}
                                        disabled={getFieldDisabledState('failureKM')}
                                    />
                                    {submitted && !formData.failureKM && (
                                        <small className="p-error">
                                            <i className="pi pi-exclamation-circle mr-1"></i>
                                            {th('preApproval.form.validation.failureKmRequired')}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Maliyet Bilgileri */}
                    <div className="col-12">
                        <h3 className="section-title mt-3">
                            <i className="pi pi-wallet mr-2"></i>
                            {th('preApproval.form.costInfoTitle')}
                        </h3>
                        <Divider />
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="estimatedPartCost" className="font-bold block mb-2">
                                <i className="pi pi-cog mr-1"></i>
                                {th('preApproval.form.estimatedPartCostLabel')} *
                            </label>
                            <InputNumber
                                id="estimatedPartCost"
                                value={formData.estimatedPartCost}
                                onChange={(e) => handleChange('estimatedPartCost', e.value)}
                                className={submitted && !formData.estimatedPartCost ? 'p-invalid' : ''}
                                placeholder="0.00"
                                disabled={getFieldDisabledState('estimatedPartCost')}
                                mode="currency"
                                currency={currency}
                                locale="tr-TR"
                            />
                            {submitted && !formData.estimatedPartCost && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('preApproval.form.validation.estimatedPartCostRequired')}
                                </small>
                            )}
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="estimatedLaborCost" className="font-bold block mb-2">
                                <i className="pi pi-users mr-1"></i>
                                {th('preApproval.form.estimatedLaborCostLabel')} *
                            </label>
                            <InputNumber
                                id="estimatedLaborCost"
                                value={formData.estimatedLaborCost}
                                onChange={(e) => handleChange('estimatedLaborCost', e.value)}
                                className={submitted && !formData.estimatedLaborCost ? 'p-invalid' : ''}
                                placeholder="0.00"
                                disabled={getFieldDisabledState('estimatedLaborCost')}
                                mode="currency"
                                currency={currency}
                                locale="tr-TR"
                            />
                            {submitted && !formData.estimatedLaborCost && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('preApproval.form.validation.estimatedLaborCostRequired')}
                                </small>
                            )}
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="otherCosts" className="font-bold block mb-2">
                                <i className="pi pi-wallet mr-1"></i>
                                {th('preApproval.form.otherCostsLabel')} *
                            </label>
                            <InputNumber
                                id="otherCosts"
                                value={formData.otherCosts}
                                onChange={(e) => handleChange('otherCosts', e.value)}
                                className={submitted && !formData.otherCosts ? 'p-invalid' : ''}
                                placeholder="0.00"
                                disabled={getFieldDisabledState('otherCosts')}
                                mode="currency"
                                currency={currency}
                                locale="tr-TR"
                            />
                            {submitted && !formData.otherCosts && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('preApproval.form.validation.otherCostsRequired')}
                                </small>
                            )}
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="totalCosts" className="font-bold block mb-2">
                                <i className="pi pi-calculator mr-1"></i>
                                {th('preApproval.form.totalCostsLabel')}
                            </label>
                            <InputNumber disabled id="totalCosts" value={formData.totalCosts} placeholder="0.00" mode="currency" currency={currency} locale="tr-TR" className="total-cost-field" />
                        </div>
                    </div>

                    {/* Periyodik Bakım */}
                    {editMode && (
                        <div className="col-12 md:col-6">
                            <label className="font-bold block mb-2">
                                <i className="pi pi-check-circle mr-1"></i>
                                {th('preApproval.form.periodicMaintenanceLabel')}
                            </label>
                            <div className="flex gap-3">
                                <div className="flex align-items-center">
                                    <RadioButton inputId="periodicYes" name="periodicMaintenanceDone" value={1} checked={formData.periodicMaintance === 1} disabled />
                                    <label htmlFor="periodicYes" className="ml-2">
                                        {th('preApproval.form.yesOption')}
                                    </label>
                                </div>
                                <div className="flex align-items-center">
                                    <RadioButton inputId="periodicNo" name="periodicMaintenanceDone" value={0} checked={formData.periodicMaintance === 0} disabled />
                                    <label htmlFor="periodicNo" className="ml-2">
                                        {th('preApproval.form.noOption')}
                                    </label>
                                </div>
                                <div className="flex align-items-center">
                                    <RadioButton inputId="periodicOther" name="periodicMaintenanceDone" value={2} checked={formData.periodicMaintance === 2} disabled />
                                    <label htmlFor="periodicOther" className="ml-2">
                                        {th('preApproval.form.otherOption')}
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Parça Kodu / Adı (read-only mod) */}
                    {!isFieldEdit && (
                        <>
                            <div className="col-12 md:col-6">
                                <label className="font-bold block mb-2">
                                    <i className="pi pi-tag mr-1"></i>
                                    {th('preApproval.form.partCodeLabel')}
                                </label>
                                <InputText value={formData.partCode} disabled className="w-full" title={formData.partCode || ''} />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="font-bold block mb-2">
                                    <i className="pi pi-box mr-1"></i>
                                    {th('preApproval.form.partNameLabel')}
                                </label>
                                <InputText value={formData.partName} disabled className="w-full" title={formData.partName || ''} />
                            </div>
                        </>
                    )}

                    {/* Onay / Red Açıklama */}
                    {(formData?.status === STATUS.REJECTED || formData?.status === STATUS.RETURNED) && (
                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label htmlFor="approvalNote" className="font-bold block mb-2">
                                    <i className="pi pi-comment mr-1"></i>
                                    {th('preApproval.form.approvalNoteLabel')}
                                </label>
                                {/* Read-only durumda InputTextarea, editable durumda InputText */}
                                {getFieldDisabledState('approvalNote') ? (
                                    <InputTextarea id="approvalNote" value={formData.approvalNote || '-'} disabled className="w-full" rows={2} style={readOnlyTextStyle} />
                                ) : (
                                    <InputText id="approvalNote" value={formData.approvalNote} onChange={(e) => handleChange('approvalNote', e.target.value)} placeholder={th('preApproval.form.placeholders.approvalNote')} className="w-full" />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Parça ve Kategori Bilgileri */}
                    <div className="col-12">
                        <h3 className="section-title mt-3">
                            <i className="pi pi-tags mr-2"></i>
                            {th('preApproval.form.partCategoryInfoTitle')}
                        </h3>
                        <Divider />
                    </div>

                    {isFieldEdit && (
                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label htmlFor="partCode" className="font-bold block mb-2">
                                    <i className="pi pi-tag mr-1"></i>
                                    {th('preApproval.form.partCodeLabel')} *
                                </label>
                                <ItemCodeDropdown
                                    value={formData.itemCode}
                                    onChange={(v) => handleChange('itemCode', v?.id || null)}
                                    className={submitted && !formData.itemCode ? 'p-invalid' : ''}
                                    placeholder={th('preApproval.form.placeholders.partCode')}
                                    disabled={getFieldDisabledState('itemCode')}
                                />
                                {submitted && !formData.itemCode && (
                                    <small className="p-error">
                                        <i className="pi pi-exclamation-circle mr-1"></i>
                                        {th('preApproval.form.validation.partCodeRequired')}
                                    </small>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="failureMode" className="font-bold block mb-2">
                                <i className="pi pi-exclamation-triangle mr-1"></i>
                                {th('preApproval.form.failureModesLabel')}
                            </label>
                            <FaultModesDropdown value={formData.failureMode} onChange={(v) => handleChange('failureMode', v)} disabled={getFieldDisabledState('failureMode')} placeholder={th('preApproval.form.placeholders.failureMode')} />
                        </div>
                    </div>

                    <CascadingDropdowns
                        mainCategoryValue={formData.mainCategory}
                        secondCategoryValue={formData.secondCategory}
                        subCategoryValue={formData.subCategory}
                        subPartCategoryValue={formData.subPartCategory}
                        onMainCategoryChange={(v) => handleChange('mainCategory', v)}
                        onSecondCategoryChange={(v) => handleChange('secondCategory', v)}
                        onSubCategoryChange={(v) => handleChange('subCategory', v)}
                        onSubPartCategoryChange={(v) => handleChange('subPartCategory', v)}
                        resetOnNewRecord={resetDropdowns}
                        isEditMode={editMode}
                        colSize="0 0 25%"
                        disabled={getFieldDisabledState('categories')}
                        readOnly={getFieldDisabledState('categories')}
                        isDisabled={getFieldDisabledState('categories')}
                    />

                    {/* Açıklama Bilgileri */}
                    <div className="col-12">
                        <h3 className="section-title mt-3">
                            <i className="pi pi-file-text mr-2"></i>
                            {th('preApproval.form.descriptionInfoTitle')}
                        </h3>
                        <Divider />
                    </div>

                    {/* customerStatement - read-only durumda autoResize + readOnlyTextStyle */}
                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="customerStatement" className="font-bold block mb-2">
                                <i className="pi pi-user mr-1"></i>
                                {th('preApproval.form.customerStatementLabel')}
                            </label>
                            <InputTextarea
                                id="customerStatement"
                                value={formData.customerStatement}
                                onChange={(e) => handleChange('customerStatement', e.target.value)}
                                onDoubleClick={handleTextareaDoubleClick}
                                placeholder={th('preApproval.form.placeholders.customerStatement')}
                                readOnly={getFieldDisabledState('customerStatement')}
                                className="w-full"
                                rows={3}
                                style={isCompletelyReadOnly ? readOnlyTextStyle : textareaStyle}
                                autoResize={isCompletelyReadOnly}
                            />
                        </div>
                    </div>

                    {/* failureReason - read-only durumda autoResize + readOnlyTextStyle */}
                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="failureReason" className="font-bold block mb-2">
                                <i className="pi pi-question-circle mr-1"></i>
                                {th('preApproval.form.failureReasonLabel')}
                            </label>
                            <InputTextarea
                                id="failureReason"
                                value={formData.failureReason}
                                onChange={(e) => handleChange('failureReason', e.target.value)}
                                onDoubleClick={handleTextareaDoubleClick}
                                placeholder={th('preApproval.form.placeholders.failureReason')}
                                readOnly={getFieldDisabledState('failureReason')}
                                className="w-full"
                                rows={3}
                                style={isCompletelyReadOnly ? readOnlyTextStyle : textareaStyle}
                                autoResize={isCompletelyReadOnly}
                            />
                        </div>
                    </div>

                    {/* resolutionSummary - read-only durumda autoResize + readOnlyTextStyle */}
                    <div className="col-12">
                        <div className="field mb-3">
                            <label htmlFor="resolutionSummary" className="font-bold block mb-2">
                                <i className="pi pi-check-square mr-1"></i>
                                {th('preApproval.form.resolutionSummaryLabel')}
                            </label>
                            <InputTextarea
                                id="resolutionSummary"
                                value={formData.resolutionSummary}
                                onChange={(e) => handleChange('resolutionSummary', e.target.value)}
                                onDoubleClick={handleTextareaDoubleClick}
                                placeholder={th('preApproval.form.placeholders.resolutionSummary')}
                                readOnly={getFieldDisabledState('resolutionSummary')}
                                className="w-full"
                                rows={3}
                                style={isCompletelyReadOnly ? readOnlyTextStyle : textareaStyle}
                                autoResize={isCompletelyReadOnly}
                            />
                        </div>
                    </div>

                    {/* Son İşlem Bilgileri */}
                    {!isFieldEdit && (
                        <>
                            <div className="col-12">
                                <h3 className="section-title mt-3">
                                    <i className="pi pi-history mr-2"></i>
                                    {th('preApproval.form.lastOperationInfoTitle')}
                                </h3>
                                <Divider />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="font-bold block mb-2">
                                    <i className="pi pi-user mr-1"></i>
                                    {th('preApproval.form.operatorLabel')}
                                </label>
                                {/* ✅ title eklendi */}
                                <InputText value={formData.modifiedName} disabled className="w-full" title={formData.modifiedName || ''} />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="font-bold block mb-2">
                                    <i className="pi pi-clock mr-1"></i>
                                    {th('preApproval.form.operationTimeLabel')}
                                </label>
                                <InputText value={formData.modifiedDate ? formatDateOnly(formData.modifiedDate) : '-'} disabled className="w-full" />
                            </div>
                        </>
                    )}

                    {editMode && <PreApprovalCommentsTable preApprovalId={currentPreApproval?.id} dialogVisible={dialogVisible} />}

                    {/* Dosya Yükleme */}
                   <div className="col-12">
                        <h3 className="section-title mt-3">
                            <i className="pi pi-paperclip mr-2"></i>
                            {th('preApproval.form.fileUploadTitle')}
                        </h3>
                        <Divider />

                        {/* ── Bayinin Dosyaları ── */}
                        <DealerDocumentsSection
                            dealerDocuments={dealerDocuments}
                            newFiles={!isWarrantyTeam ? newFiles : []}
                            onFilesChange={!isWarrantyTeam ? setNewFiles : undefined}
                            onDownloadDocument={handleDownloadDocument}
                            onDownloadAll={() => handleDownloadAllAsZip(MODULE_CODES.DEALER)}
                            onDeleteDocument={
                            (currentStatus === STATUS.DRAFT || currentStatus === STATUS.RETURNED) && !isWarrantyTeam
                                ? handleDeleteExistingDocument
                                : null
                            }
                            isLoading={isLoadingDocuments}
                            isWarrantyTeam={isWarrantyTeam}
                            canUpload={!isWarrantyTeam && canUpload}
                            isProcessingFiles={isProcessingFiles}
                            th={th}
                        />

                        {/* ── Garanti Ekibinin Dosyaları ── */}
                        {currentStatus !== STATUS.DRAFT && (
                            <>
                                <Divider className="my-4" />
                                <WarrantyDocumentsSection
                                    warrantyDocuments={warrantyDocuments}
                                    newFiles={isWarrantyTeam ? newFiles : []}
                                    onFilesChange={isWarrantyTeam ? setNewFiles : undefined}
                                    onDownloadDocument={handleDownloadDocument}
                                    onDownloadAll={() => handleDownloadAllAsZip(MODULE_CODES.WARRANTY)}
                                    onDeleteDocument={
                                        currentStatus === STATUS.WAITING && isWarrantyTeam
                                        ? handleDeleteExistingDocument
                                        : null
                                }
                                    isLoading={isLoadingDocuments}
                                    isWarrantyTeam={isWarrantyTeam}
                                    canUpload={isWarrantyTeam && canUpload}
                                    isProcessingFiles={isProcessingFiles}
                                    th={th}
                                />
                            </>
                        )}
                    </div>
                </div>
            </Dialog>
            <ReasonDialog visible={reasonDialogVisible} onHide={() => setReasonDialogVisible(false)} onConfirm={handleReasonConfirm} type={reasonDialogType} />
        </>
    );
};

export default PreApprovalForm;
